import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Feather, ShieldCheck, Sparkle, HandHeart, Leaf, Sun, Buildings, House, Storefront, Briefcase, ArrowUpRight } from '@phosphor-icons/react';
import api from '../utils/api';
import ProductCard from '../components/ProductCard';
import ImpactTracker from '../components/ImpactTracker';

// ————————————————————————————————————————————————————————
// Reusable section header — Belanosima H2 + muted subtitle
// ————————————————————————————————————————————————————————
const SectionHeader = ({ kicker, title, subtitle, align = 'center', className = '' }) => (
  <div className={`${align === 'center' ? 'text-center max-w-2xl mx-auto' : 'max-w-2xl'} ${className}`}>
    {kicker && (
      <p className="text-[11px] uppercase tracking-[0.3em] text-[#BC64AA] font-semibold mb-4">{kicker}</p>
    )}
    <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#641b4d] tracking-tight leading-[1.05]">
      {title}
    </h2>
    {subtitle && <p className="text-base md:text-lg text-[#7A5E75] mt-5 leading-relaxed">{subtitle}</p>}
  </div>
);

// ————————————————————————————————————————————————————————
// Data — 4 Value Props, 6 Spaces, 5 Material Benefits, 4 Project Segments
// ————————————————————————————————————————————————————————
const valueProps = [
  { icon: Feather, title: 'Lightweight', copy: 'Effortless to move, style and rearrange around your home.' },
  { icon: ShieldCheck, title: 'Weather-proof', copy: 'Rain, sun or seasons — the finish stays true. Indoor and outdoor ready.' },
  { icon: Sparkle, title: 'Premium Finish', copy: 'Precision-moulded shapes with matte, glossy and grainy textures.' },
  { icon: HandHeart, title: 'Made with purpose', copy: 'Hand-finished by women earning their own livelihood.' }
];

const spaces = [
  { icon: Sun, label: 'Balcony', image: 'https://customer-assets.emergentagent.com/job_pot-commerce-1/artifacts/xyg5veag_Balcony.png' },
  { icon: House, label: 'Living Room', image: 'https://customer-assets.emergentagent.com/job_pot-commerce-1/artifacts/jkphaeg7_Living%20room.png' },
  { icon: Leaf, label: 'Outdoor & Garden', image: 'https://customer-assets.emergentagent.com/job_pot-commerce-1/artifacts/y0m0neqg_Outdoor%20%26%20Garden.png' },
  { icon: Buildings, label: 'Entryway', image: 'https://customer-assets.emergentagent.com/job_pot-commerce-1/artifacts/9nfmoc0p_Entryway.png' },
  { icon: Storefront, label: 'Café & Hospitality', image: 'https://customer-assets.emergentagent.com/job_pot-commerce-1/artifacts/05f4g4lk_P_38.png' },
  { icon: Briefcase, label: 'Office & Workspace', image: 'https://customer-assets.emergentagent.com/job_pot-commerce-1/artifacts/sgl4evhc_Office%20%26%20Workspace.png' }
];

const frpBenefits = [
  { num: '01', h: 'Lightweight, yet strong', p: 'A fraction of the weight of ceramic or concrete — without giving up structural strength.' },
  { num: '02', h: 'Weather resistant', p: 'Handles direct sun, monsoon rain and dry winds without cracking, warping or fading.' },
  { num: '03', h: 'Won\'t crack or shatter', p: 'Unlike terracotta or ceramic, FRP stays intact through years of use and moving.' },
  { num: '04', h: 'Refined finishes', p: 'Automotive-grade paint in matte, glossy or grainy textures — engineered to look premium.' },
  { num: '05', h: 'Indoor + outdoor', p: 'One material, every space. From living rooms to balconies to open patios.' }
];

const projectSegments = [
  'Architects & Interior Designers',
  'Cafés & Hospitality',
  'Apartments, Builders & Offices',
  'Bulk, Gifting & Commercial Projects'
];

// ————————————————————————————————————————————————————————
// HOMEPAGE
// ————————————————————————————————————————————————————————
const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);

  useEffect(() => {
    api.get('/products')
      .then((r) => setFeaturedProducts(r.data.slice(0, 4)))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="home-page" className="min-h-screen bg-[#F4F4F4]">

      {/* ============================================================
          1. HERO — Two-column: Brand + type on left, product visual on right
          ============================================================ */}
      <section className="relative bg-[#641b4d] overflow-hidden">
        {/* Decorative background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -left-40 w-[700px] h-[700px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(188,100,170,0.28) 0%, rgba(188,100,170,0) 70%)' }} />
          <div className="absolute inset-0 opacity-[0.06]" style={{
            backgroundImage: 'radial-gradient(rgba(255,255,255,0.7) 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-[1.15fr_1fr] gap-12 lg:gap-20 items-center pt-24 pb-24 lg:pt-28 lg:pb-28">
          {/* Left — brand + copy */}
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <img
              src="/logos/veveru_white.svg"
              alt="Veveru"
              className="w-[80%] md:w-[68%] max-w-[440px] h-auto -mb-16 md:-mb-20 -mt-8 md:-mt-12 -ml-6 md:-ml-8"
            />
            <div className="flex items-center gap-3 mb-10 md:mb-14">
              <span className="w-10 h-px bg-[#FFD454]" />
              <span className="text-xs md:text-sm uppercase tracking-[0.35em] text-[#FFD454] font-semibold">
                Different by Design
              </span>
            </div>

            <h1
              data-testid="hero-heading"
              className="text-white leading-[0.98] tracking-tight mb-8"
              style={{ fontFamily: 'Belanosima, sans-serif', fontWeight: 600, fontSize: 'clamp(2.75rem, 6.5vw, 5.5rem)' }}
            >
              Premium planters for<br />modern <span className="text-[#FFD454]">Indian homes</span>.
            </h1>

            <p className="text-base md:text-lg text-white/75 mb-12 max-w-lg leading-relaxed">
              Lightweight, weather-proof FRP planters designed in-house and hand-finished for the spaces you love — from balconies to living rooms to open patios.
            </p>

            <div className="flex flex-wrap items-center gap-4">
              <Link
                to="/products"
                data-testid="hero-shop-btn"
                className="inline-flex items-center gap-3 bg-[#FFD454] text-[#641b4d] px-8 py-4 text-xs md:text-sm font-semibold uppercase tracking-[0.15em] hover:bg-white transition-colors"
              >
                Shop the Collection
                <ArrowRight size={16} weight="bold" />
              </Link>
              <Link
                to="/bulk-inquiry"
                data-testid="hero-projects-btn"
                className="inline-flex items-center gap-3 text-white text-xs md:text-sm font-semibold uppercase tracking-[0.15em] px-4 py-4 border-b border-white/40 hover:border-[#FFD454] hover:text-[#FFD454] transition-colors"
              >
                For Projects
                <ArrowUpRight size={16} weight="bold" />
              </Link>
            </div>
          </motion.div>

          {/* Right — hero product visual (offset for depth) */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="relative hidden lg:block"
          >
            {/* Yellow accent card behind product */}
            <div className="absolute -top-6 -left-6 w-full h-full bg-[#FFD454]" />
            <div className="relative w-full aspect-[4/5] overflow-hidden bg-white">
              <img
                src="https://customer-assets.emergentagent.com/job_pot-commerce-1/artifacts/pozkiyz9_P_63.png"
                alt="Veveru premium planter in a styled home setting"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Floating tag */}
            <div className="absolute -bottom-4 -right-4 bg-white text-[#641b4d] text-[10px] font-semibold uppercase tracking-[0.2em] px-4 py-2 shadow-lg">
              Handcrafted in Coimbatore
            </div>
          </motion.div>
        </div>

        {/* Marquee strip */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-white/10 py-4 overflow-hidden bg-[#641b4d]">
          <div className="flex items-center gap-10 px-6 text-white/50 text-[10px] uppercase tracking-[0.3em] whitespace-nowrap">
            <span>Fiber Reinforced Polymer</span>
            <span className="w-1 h-1 rounded-full bg-[#FFD454]" />
            <span>Indoor + Outdoor</span>
            <span className="w-1 h-1 rounded-full bg-[#FFD454]" />
            <span>3-Year Paint Warranty</span>
            <span className="w-1 h-1 rounded-full bg-[#FFD454]" />
            <span>Pan-India Shipping</span>
            <span className="w-1 h-1 rounded-full bg-[#FFD454]" />
            <span>Women-led Manufacturing</span>
          </div>
        </div>
      </section>

      {/* ============================================================
          2. WHY VEVERU — 4 value props
          ============================================================ */}
      <section className="py-24 lg:py-32 px-6 bg-[#F4F4F4]">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            kicker="Why Veveru"
            title="Designed to outlast trends."
            subtitle="Every Veveru planter is engineered for real Indian homes and hand-finished for a look you'll live with for years."
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-16">
            {valueProps.map((vp, i) => {
              const Icon = vp.icon;
              return (
                <motion.div
                  key={vp.title}
                  data-testid={`value-prop-${i}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.5 }}
                  className="bg-white border border-[#E5E2DC] p-8 hover:border-[#641b4d] hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-[#641b4d] text-[#FFD454] mb-6">
                    <Icon size={22} weight="light" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#641b4d] mb-2">{vp.title}</h3>
                  <p className="text-sm text-[#7A5E75] leading-relaxed">{vp.copy}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================================================
          3. SHOP BY SPACE
          ============================================================ */}
      <section className="py-24 lg:py-32 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
            <SectionHeader
              kicker="Shop by Space"
              title="Made for every corner of your home."
              subtitle="Whether it's the balcony you unwind on or the café you're building — find planters that belong there."
              align="left"
            />
            <Link
              to="/products"
              data-testid="all-spaces-link"
              className="inline-flex items-center gap-2 text-[#641b4d] text-xs font-semibold uppercase tracking-[0.2em] border-b border-[#641b4d] pb-1 hover:gap-3 transition-all self-start"
            >
              View All Products
              <ArrowRight size={14} weight="bold" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {spaces.map((space, i) => {
              const Icon = space.icon;
              return (
                <motion.div
                  key={space.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05, duration: 0.5 }}
                >
                  <Link
                    to="/products"
                    data-testid={`space-card-${space.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className="group relative block aspect-[4/5] overflow-hidden bg-[#641b4d]"
                  >
                    <img
                      src={space.image}
                      alt={space.label}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#641b4d]/85 via-[#641b4d]/30 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-5 md:p-6">
                      <Icon size={20} weight="light" className="text-[#FFD454] mb-3" />
                      <h3 className="text-white text-lg md:text-xl font-semibold leading-tight">{space.label}</h3>
                      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-white/70 mt-2 group-hover:text-[#FFD454] transition-colors">
                        Explore <ArrowRight size={11} weight="bold" />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================================================
          4. FEATURED PRODUCTS
          ============================================================ */}
      <section className="py-24 lg:py-32 px-6 bg-[#F4F4F4]">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            kicker="Bestsellers"
            title="Signature pieces our customers love."
            subtitle="Hand-picked planters that define the Veveru look."
          />

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mt-16">
            {featuredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>

          <div className="text-center mt-14">
            <Link
              to="/products"
              data-testid="view-all-products-button"
              className="btn-brand-outline inline-flex items-center gap-2"
            >
              View The Full Collection
              <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============================================================
          5. WHY FRP — Material advantages, elegant split layout
          ============================================================ */}
      <section className="py-24 lg:py-32 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-[1fr_1.4fr] gap-14 lg:gap-24">
            {/* Left — heading + image */}
            <div>
              <SectionHeader
                kicker="Made With FRP"
                title="A smarter material. A better planter."
                align="left"
              />
              <p className="text-base text-[#7A5E75] leading-relaxed mt-6 mb-10 max-w-md">
                FRP — fibre-reinforced polymer — is engineered to give you the strength of stone and the ease of plastic, wrapped in an automotive-grade finish.
              </p>
              <div className="aspect-[4/5] overflow-hidden max-w-xs">
                <img
                  src="https://customer-assets.emergentagent.com/job_pot-commerce-1/artifacts/0tm8s85r_1.png"
                  alt="Veveru artisan hand-finishing an FRP planter"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Right — 5 benefits as horizontal rows */}
            <div className="space-y-8 md:space-y-10">
              {frpBenefits.map((b, i) => (
                <motion.div
                  key={b.num}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.5 }}
                  className="flex gap-6 md:gap-8 pb-8 md:pb-10 border-b border-[#E5E2DC] last:border-b-0"
                >
                  <span className="text-2xl md:text-3xl text-[#BC64AA] font-semibold tabular-nums leading-none shrink-0" style={{ fontFamily: 'Belanosima, sans-serif' }}>
                    {b.num}
                  </span>
                  <div>
                    <h3 className="text-xl md:text-2xl font-semibold text-[#641b4d] mb-2 leading-tight">{b.h}</h3>
                    <p className="text-sm md:text-base text-[#7A5E75] leading-relaxed">{b.p}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          6. IMPACT STORY — Warm, concise, supports the brand
          ============================================================ */}
      <section className="relative py-24 lg:py-32 px-6 bg-[#641b4d] text-white overflow-hidden">
        <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(188,100,170,0.35) 0%, rgba(188,100,170,0) 70%)' }} />

        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <div className="aspect-[4/5] max-w-lg overflow-hidden">
            <img
              src="https://customer-assets.emergentagent.com/job_pot-commerce-1/artifacts/9gze9wqf_IMG_7402.jpeg"
              alt="A Veveru artisan"
              className="w-full h-full object-cover"
              style={{ objectPosition: '50% 20%' }}
            />
          </div>

          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#FFD454] font-semibold mb-6">Our Story</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-[1.1] tracking-tight mb-8">
              Behind every Veveru planter is a woman building her own future.
            </h2>
            <p className="text-base md:text-lg text-white/75 leading-relaxed mb-10 max-w-lg">
              We didn&apos;t want to just make planters. We wanted to make them differently — engineered in-house, hand-finished by women trained to manufacture, price and sell.
              <br /><br />
              Every purchase supports that. Quietly, meaningfully, without needing a badge.
            </p>
            <Link
              to="/impact"
              data-testid="read-story-btn"
              className="inline-flex items-center gap-3 text-white text-xs md:text-sm font-semibold uppercase tracking-[0.15em] border-b border-white/40 pb-2 hover:border-[#FFD454] hover:text-[#FFD454] transition-colors"
            >
              Read Our Story
              <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============================================================
          7. FOR PROJECTS — B2B enquiry section
          ============================================================ */}
      <section className="py-24 lg:py-32 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-[1fr_1.2fr] gap-14 lg:gap-24 items-start">
            {/* Left */}
            <div>
              <SectionHeader
                kicker="For Projects"
                title="Building something ambitious?"
                subtitle="Whether you're specifying planters for a hotel lobby, styling a café patio, or fitting out apartments — our project team will help you get the pieces, quantities, colours and finishes right."
                align="left"
              />
              <Link
                to="/bulk-inquiry"
                data-testid="project-enquiry-btn"
                className="btn-brand-primary inline-flex items-center gap-2 mt-10"
                style={{ backgroundColor: '#641b4d', color: '#FFFFFF' }}
              >
                Start a Project Enquiry
                <ArrowRight size={16} weight="bold" />
              </Link>
            </div>

            {/* Right — segment list */}
            <div className="bg-[#F4F4F4] border-l-2 border-[#FFD454] p-8 md:p-12">
              <p className="text-[11px] uppercase tracking-[0.3em] text-[#BC64AA] font-semibold mb-6">Who We Work With</p>
              <ul className="space-y-5">
                {projectSegments.map((seg, i) => (
                  <li key={seg} className="flex items-start gap-4">
                    <span className="text-xs text-[#BC64AA] font-semibold tabular-nums pt-1" style={{ fontFamily: 'Belanosima, sans-serif' }}>
                      0{i + 1}
                    </span>
                    <span className="text-base md:text-lg text-[#641b4d] font-medium">{seg}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          8. LIVE IMPACT TRACKER — trust marker before final CTA
          ============================================================ */}
      <ImpactTracker variant="dark" title="Every pot you buy changes a life." />

      {/* ============================================================
          9. FINAL CTA — before footer
          ============================================================ */}
      <section className="py-24 lg:py-32 px-6 bg-[#F4F4F4]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#BC64AA] font-semibold mb-5">Ready to Style Your Space?</p>
          <h2 className="text-3xl md:text-5xl font-semibold text-[#641b4d] tracking-tight leading-[1.05] mb-8">
            Bring home something<br />different by design.
          </h2>
          <div className="flex flex-wrap justify-center gap-4 mt-10">
            <Link
              to="/products"
              data-testid="final-cta-shop"
              className="btn-brand-primary inline-flex items-center gap-2"
              style={{ backgroundColor: '#641b4d', color: '#FFFFFF' }}
            >
              Explore The Collection
              <ArrowRight size={16} weight="bold" />
            </Link>
            <Link
              to="/custom-inquiry"
              data-testid="final-cta-custom"
              className="btn-brand-outline inline-flex items-center gap-2"
            >
              Design Your Own
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
